import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { getPdfAssets, addLogo, drawCompanyBlock, applyWatermarkAllPages } from "../lib/pdf";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { DollarSign, FileText, Plus, Edit, Trash2, Check, ChevronsUpDown, Download } from "lucide-react";
import { exportToExcel } from "../lib/excel";
import { API } from "../lib/api";
import { sortByName } from "../lib/sortByName";
import { SearchableSelect } from "../components/SearchableSelect";
import { DatePicker } from "../components/DatePicker";
import { useConfirm } from "../components/ConfirmDialog";

const Diarias = () => {
  const [diarias, setDiarias] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [prestadores, setPrestadores] = useState([]);
  const [funcoes, setFuncoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    data: '', // Data início (obrigatória)
    dataFim: '', // Data fim (opcional - para lançamento em lote)
    cliente_id: '',
    prestador_id: '',
    funcao_id: '',
    horario: '', // Campo legado
    horario_inicio: '', // Novo campo
    horario_saida: '', // Novo campo
    jornada_padrao_horas: '', // Novo campo (8, 10 ou 12)
    observacoes: '',
    loteMode: false // Modo lote ativado quando data fim é preenchida
  });
  const [editingId, setEditingId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOverrideDialogOpen, setIsOverrideDialogOpen] = useState(false);
  const [selectedDiaria, setSelectedDiaria] = useState(null);
  const [overrideData, setOverrideData] = useState({
    campo_alterado: 'valor_pagar',
    valor_novo: '',
    motivo: ''
  });
  
  // Provider autocomplete state
  const [providerComboboxOpen, setProviderComboboxOpen] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  const [providerSearchValue, setProviderSearchValue] = useState('');
  
  // Filtros da página de diárias
  const [filtros, setFiltros] = useState({
    prestador: 'todos',
    cliente: 'todos',
    statusPago: 'todos',
    dataInicio: '',
    dataFim: ''
  });

  const fetchData = async () => {
    try {
      const [diariasRes, clientesRes, prestadoresRes, funcoesRes] = await Promise.all([
        axios.get(`${API}/diarias`),
        axios.get(`${API}/clientes`),
        axios.get(`${API}/prestadores`),
        axios.get(`${API}/funcoes`)
      ]);
      
      setDiarias(diariasRes.data);
      setClientes(clientesRes.data.filter(c => c.status === 'ativo'));
      // Ordenar prestadores ativos alfabeticamente por nome
      const prestadoresAtivos = prestadoresRes.data
        .filter(p => p.status === 'ativo')
        .sort(sortByName);
      setPrestadores(prestadoresAtivos);
      // Ordenar funções alfabeticamente por nome
      const funcoesAtivas = funcoesRes.data
        .filter(f => f.status === 'ativo')
        .sort(sortByName);
      setFuncoes(funcoesAtivas);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const carregar = async () => { await fetchData(); };
    carregar();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.cliente_id || !formData.prestador_id || !formData.funcao_id) {
      toast.error("Cliente, Prestador e Função são obrigatórios");
      return;
    }

    if (!formData.data) {
      toast.error("Data de início é obrigatória");
      return;
    }
    
    // Validar jornada se horários forem preenchidos
    if ((formData.horario_inicio || formData.horario_saida) && !formData.jornada_padrao_horas) {
      toast.error("Selecione a Jornada Padrão quando preencher horários");
      return;
    }
    
    if (formData.horario_inicio && formData.horario_saida && formData.horario_inicio === formData.horario_saida) {
      toast.error("Horário de saída não pode ser igual ao de início");
      return;
    }

    try {
      if (editingId) {
        // Para edição, enviamos os dados incluindo os novos campos de horário
        const dataToSend = {
          data: formData.data,
          cliente_id: formData.cliente_id,
          prestador_id: formData.prestador_id,
          funcao_id: formData.funcao_id,
          horario_inicio: formData.horario_inicio || null,
          horario_saida: formData.horario_saida || null,
          jornada_padrao_horas: formData.jornada_padrao_horas ? parseInt(formData.jornada_padrao_horas) : null,
          observacoes: formData.observacoes
        };
        
        await axios.put(`${API}/diarias/${editingId}`, dataToSend);
        toast.success("Diária atualizada com recálculo automático!");
      } else {
        // Verificar se é lançamento em lote ou individual
        const isLote = formData.dataFim && formData.dataFim !== formData.data;
        
        if (isLote) {
          // Lançamento em lote
          await handleLancamentoLote();
        } else {
          // Lançamento individual
          await axios.post(`${API}/diarias`, {
            data: formData.data,
            cliente_id: formData.cliente_id,
            prestador_id: formData.prestador_id,
            funcao_id: formData.funcao_id,
            horario_inicio: formData.horario_inicio || null,
            horario_saida: formData.horario_saida || null,
            jornada_padrao_horas: formData.jornada_padrao_horas ? parseInt(formData.jornada_padrao_horas) : null,
            observacoes: formData.observacoes
          });
          toast.success("Diária criada com cálculo automático!");
        }
      }
      
      setFormData({
        data: '',
        dataFim: '',
        cliente_id: '',
        prestador_id: '',
        funcao_id: '',
        horario: '',
        horario_inicio: '',
        horario_saida: '',
        jornada_padrao_horas: '',
        observacoes: '',
        loteMode: false
      });
      setEditingId(null);
      setIsDialogOpen(false);
      setProviderComboboxOpen(false);
      setProviderSearchValue('');
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erro ao salvar diária";
      toast.error(errorMsg);
    }
  };

  // Função para lançamento em lote
  const handleLancamentoLote = async () => {
    const dataInicio = new Date(formData.data);
    const dataFim = new Date(formData.dataFim);
    
    if (dataFim < dataInicio) {
      toast.error("Data fim não pode ser menor que data início");
      return;
    }

    // Calcular quantos dias de diferença
    const diffTime = Math.abs(dataFim - dataInicio);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 para incluir o último dia
    
    if (diffDays > 31) {
      toast.error("Não é possível lançar mais de 31 dias de uma vez");
      return;
    }

    // Confirmar com o usuário
    const confirmacao = await confirm({
      title: "Confirmar lançamento em lote?",
      description: (
        <span className="whitespace-pre-line">
          {`Confirma o lançamento de ${diffDays} diárias em lote?\n\n` +
            `Prestador: ${getPrestadorNome(formData.prestador_id)}\n` +
            `Cliente: ${getClienteNome(formData.cliente_id)}\n` +
            `Função: ${getFuncaoNome(formData.funcao_id)}\n` +
            `Período: ${formData.data} até ${formData.dataFim}` +
            (formData.jornada_padrao_horas ? `\nJornada: ${formData.jornada_padrao_horas}h` : '')}
        </span>
      ),
      confirmText: "Lançar em lote",
      destructive: false,
    });

    if (!confirmacao) {
      return;
    }

    try {
      let diariasCreated = 0;
      let diariasError = 0;
      
      // Criar diárias para cada dia no período
      for (let d = new Date(dataInicio); d <= dataFim; d.setDate(d.getDate() + 1)) {
        const dataFormatada = d.toISOString().split('T')[0];
        
        try {
          await axios.post(`${API}/diarias`, {
            data: dataFormatada,
            cliente_id: formData.cliente_id,
            prestador_id: formData.prestador_id,
            funcao_id: formData.funcao_id,
            horario_inicio: formData.horario_inicio || null,
            horario_saida: formData.horario_saida || null,
            jornada_padrao_horas: formData.jornada_padrao_horas ? parseInt(formData.jornada_padrao_horas) : null,
            observacoes: formData.observacoes || `Lançamento em lote - ${new Date().toLocaleDateString('pt-BR')}`
          });
          diariasCreated++;
        } catch (error) {
          diariasError++;
          console.error(`Erro ao criar diária para ${dataFormatada}:`, error);
        }
      }
      
      // Feedback para o usuário
      if (diariasError === 0) {
        toast.success(`✅ Lote concluído! ${diariasCreated} diárias criadas com sucesso.`);
      } else {
        toast.error(`⚠️ Lote parcialmente concluído: ${diariasCreated} criadas, ${diariasError} falharam. Verifique se já existem diárias para algumas datas.`);
      }
      
    } catch (error) {
      toast.error("Erro durante lançamento em lote");
      console.error('Erro no lançamento em lote:', error);
    }
  };

  const handleEdit = (diaria) => {
    setFormData({
      data: diaria.data,
      dataFim: '', // Não aplicável para edição
      cliente_id: diaria.cliente_id,
      prestador_id: diaria.prestador_id,
      funcao_id: diaria.funcao_id,
      horario: diaria.horario || '',
      horario_inicio: diaria.horario_inicio || '',
      horario_saida: diaria.horario_saida || '',
      jornada_padrao_horas: diaria.jornada_padrao_horas || '',
      observacoes: diaria.observacoes || '',
      loteMode: false
    });
    setEditingId(diaria.id);
    setIsDialogOpen(true);
  };

  const handleOverride = (diaria) => {
    setSelectedDiaria(diaria);
    setOverrideData({
      campo_alterado: 'valor_pagar',
      valor_novo: '',
      motivo: ''
    });
    setIsOverrideDialogOpen(true);
  };

  const handleOverrideSubmit = async (e) => {
    e.preventDefault();
    
    if (!overrideData.valor_novo || !overrideData.motivo) {
      toast.error("Valor e motivo são obrigatórios para override");
      return;
    }

    try {
      await axios.put(`${API}/diarias/${selectedDiaria.id}/override`, {
        diaria_id: selectedDiaria.id,
        campo_alterado: overrideData.campo_alterado,
        valor_novo: parseFloat(overrideData.valor_novo),
        motivo: overrideData.motivo
      });
      
      toast.success("Override aplicado com sucesso!");
      setIsOverrideDialogOpen(false);
      setSelectedDiaria(null);
      fetchData();
    } catch (error) {
      toast.error("Erro ao aplicar override");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Excluir diária?",
      description: "Tem certeza que deseja deletar esta diária? Esta ação não pode ser desfeita e afetará os relatórios.",
      confirmText: "Excluir",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/diarias/${id}`);
      toast.success("Diária deletada com sucesso!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao deletar diária");
    }
  };

  const togglePagamento = async (diariaId, novoPago) => {
    try {
      await axios.put(`${API}/diarias/${diariaId}`, { pago: novoPago });
      toast.success(novoPago ? "Diária marcada como paga!" : "Diária marcada como pendente!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar status de pagamento");
      console.error('Erro ao atualizar pagamento:', error);
    }
  };

  const getClienteNome = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente ? cliente.nome : 'Cliente não encontrado';
  };

  const getPrestadorNome = (prestadorId) => {
    const prestador = prestadores.find(p => p.id === prestadorId);
    return prestador ? prestador.nome : 'Prestador não encontrado';
  };

  const getFuncaoNome = (funcaoId) => {
    const funcao = funcoes.find(f => f.id === funcaoId);
    return funcao ? funcao.nome : 'Função não encontrada';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função para filtrar diárias
  const getDiariasFiltradas = () => {
    return diarias.filter(diaria => {
      // Filtro por prestador
      if (filtros.prestador !== 'todos' && diaria.prestador_id !== filtros.prestador) {
        return false;
      }
      
      // Filtro por cliente
      if (filtros.cliente !== 'todos' && diaria.cliente_id !== filtros.cliente) {
        return false;
      }
      
      // Filtro por status pago
      if (filtros.statusPago !== 'todos') {
        const isPago = diaria.pago === true;
        if (filtros.statusPago === 'pago' && !isPago) return false;
        if (filtros.statusPago === 'pendente' && isPago) return false;
      }
      
      // Filtro por data
      if (filtros.dataInicio || filtros.dataFim) {
        const dataDiaria = diaria.data; // Manter como string YYYY-MM-DD
        const dataInicio = filtros.dataInicio;
        const dataFim = filtros.dataFim;
        
        // Comparar as datas como strings no formato YYYY-MM-DD
        if (dataInicio && dataDiaria < dataInicio) return false;
        if (dataFim && dataDiaria > dataFim) return false;
      }
      
      return true;
    }).sort((a, b) => new Date(b.data) - new Date(a.data)); // Manter ordenação por data decrescente
  };

  // Get filtered and sorted providers for autocomplete (busca por nome OU matrícula)
  const getFilteredProviders = () => {
    const termo = providerSearchValue.toLowerCase().trim();
    return prestadores
      .filter(provider => {
        if (!termo) return true;
        const nome = (provider.nome || '').toLowerCase();
        const matricula = (provider.numero_cadastro || '').toLowerCase();
        return nome.includes(termo) || matricula.includes(termo);
      })
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  };

  // Rótulo "P-000002 — NOME" para o prestador
  const getProviderLabel = (provider) => {
    if (!provider) return '';
    return provider.numero_cadastro
      ? `${provider.numero_cadastro} — ${provider.nome}`
      : provider.nome;
  };

  // Get selected provider display value
  const getSelectedProviderValue = () => {
    if (!formData.prestador_id) return "Selecione um prestador";
    const provider = prestadores.find(p => p.id === formData.prestador_id);
    return provider ? getProviderLabel(provider) : "Prestador não encontrado";
  };

  // Função para exportar diárias em Excel (.xlsx)
  const exportDiariasExcel = () => {
    const diariasFiltradas = getDiariasFiltradas();

    if (diariasFiltradas.length === 0) {
      toast.error("Nenhuma diária encontrada para exportar");
      return;
    }

    const formatarData = (dataStr) => {
      if (!dataStr) return '';
      const [ano, mes, dia] = dataStr.split('-');
      return `${dia}/${mes}/${ano}`;
    };

    const rows = diariasFiltradas.map(diaria => ({
      'Data': formatarData(diaria.data),
      'Cliente': getClienteNome(diaria.cliente_id),
      'Prestador': getPrestadorNome(diaria.prestador_id),
      'Função': getFuncaoNome(diaria.funcao_id),
      'Valor Pagar': Number((diaria.valor_pagar || 0).toFixed(2)),
      'Valor Cobrar': Number((diaria.valor_cobrar || 0).toFixed(2)),
      'Margem': Number(((diaria.valor_cobrar || 0) - (diaria.valor_pagar || 0)).toFixed(2)),
      'Pago': diaria.pago ? 'Sim' : 'Não',
      'Observações': (diaria.observacoes || '').replace(/\n/g, ' '),
    }));

    const hoje = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    exportToExcel(rows, `diarias-${hoje}`, 'Diárias');
    toast.success("Relatório Excel exportado com sucesso!");
  };

  // Função para exportar diárias em PDF
  const exportDiariasPDF = async () => {
    const diariasFiltradas = getDiariasFiltradas();
    
    if (diariasFiltradas.length === 0) {
      toast.error("Nenhuma diária encontrada para exportar");
      return;
    }

    // Criar PDF em modo paisagem para caber mais colunas
    const { logoDataUrl, empresa } = await getPdfAssets();
    const doc = new jsPDF('landscape');
    
    // Logo + dados da empresa (empresa alinhada à direita)
    addLogo(doc, logoDataUrl, 10, 8, 30, 18);
    drawCompanyBlock(doc, empresa, doc.internal.pageSize.getWidth() - 10, 12, { align: 'right', nameSize: 11 });
    
    // Título
    doc.setFontSize(16);
    doc.text('Relatório de Diárias', 50, 20);
    
    // Data de geração
    doc.setFontSize(10);
    doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 50, 28);
    
    // Informações dos filtros aplicados
    let yPos = 40;
    doc.setFontSize(8);
    doc.text('Filtros Aplicados:', 10, yPos);
    yPos += 5;
    
    if (filtros.prestador !== 'todos') {
      doc.text(`• Prestador: ${getPrestadorNome(filtros.prestador)}`, 10, yPos);
      yPos += 4;
    }
    if (filtros.cliente !== 'todos') {
      doc.text(`• Cliente: ${getClienteNome(filtros.cliente)}`, 10, yPos);
      yPos += 4;
    }
    if (filtros.statusPago !== 'todos') {
      doc.text(`• Status: ${filtros.statusPago}`, 10, yPos);
      yPos += 4;
    }
    if (filtros.dataInicio || filtros.dataFim) {
      const formatarData = (dataStr) => {
        if (!dataStr) return null;
        const [ano, mes, dia] = dataStr.split('-');
        return `${dia}/${mes}/${ano}`;
      };
      
      const dataInicioFormatada = filtros.dataInicio ? formatarData(filtros.dataInicio) : 'início';
      const dataFimFormatada = filtros.dataFim ? formatarData(filtros.dataFim) : 'fim';
      
      const periodo = `${dataInicioFormatada} até ${dataFimFormatada}`;
      doc.text(`• Período: ${periodo}`, 10, yPos);
      yPos += 4;
    }
    
    yPos += 10;
    
    // Cabeçalho da tabela - ajustado para modo paisagem com observações
    doc.setFontSize(8);
    const startY = yPos;
    const colWidths = [22, 38, 38, 28, 24, 24, 93]; // Larguras ajustadas para paisagem com observações maiores
    const headers = ['Data', 'Cliente', 'Prestador', 'Função', 'Valor Pagar', 'Valor Cobrar', 'Observações'];
    
    const drawHeader = (y) => {
      let x = 10;
      doc.setFillColor(240, 240, 240);
      headers.forEach((header, index) => {
        doc.rect(x, y, colWidths[index], 8, 'FD');
        doc.text(header, x + 2, y + 5);
        x += colWidths[index];
      });
      return y + 8;
    };
    
    yPos = drawHeader(startY);
    
    // Dados da tabela
    let totalPagar = 0;
    let totalCobrar = 0;
    const lineHeight = 4; // Altura de cada linha de texto
    const cellPadding = 1;
    
    diariasFiltradas.forEach((diaria, index) => {
      const formatarData = (dataStr) => {
        if (!dataStr) return '';
        const [ano, mes, dia] = dataStr.split('-');
        return `${dia}/${mes}/${ano}`;
      };
      
      // Preparar dados da linha
      const observacoes = diaria.observacoes || '';
      
      // Quebrar observações em múltiplas linhas (máximo ~45 caracteres por linha para caber na coluna)
      const maxCharsPerLine = 45;
      const obsLines = [];
      if (observacoes) {
        let remaining = observacoes;
        while (remaining.length > 0) {
          if (remaining.length <= maxCharsPerLine) {
            obsLines.push(remaining);
            break;
          }
          // Encontrar último espaço antes do limite
          let breakPoint = remaining.lastIndexOf(' ', maxCharsPerLine);
          if (breakPoint <= 0) breakPoint = maxCharsPerLine;
          obsLines.push(remaining.substring(0, breakPoint));
          remaining = remaining.substring(breakPoint).trim();
        }
      }
      
      // Calcular altura da linha baseado nas observações (mínimo 1 linha, máximo 4 linhas)
      const numLines = Math.min(Math.max(obsLines.length, 1), 4);
      const rowHeight = (numLines * lineHeight) + (cellPadding * 2);
      
      // Verificar se precisa de nova página
      if (yPos + rowHeight > 180) {
        doc.addPage('landscape');
        yPos = drawHeader(20);
      }
      
      const rowData = [
        formatarData(diaria.data),
        getClienteNome(diaria.cliente_id),
        getPrestadorNome(diaria.prestador_id),
        getFuncaoNome(diaria.funcao_id),
        `R$ ${diaria.valor_pagar.toFixed(2).replace('.', ',')}`,
        `R$ ${diaria.valor_cobrar.toFixed(2).replace('.', ',')}`
      ];
      
      // Desenhar células da linha
      let xPos = 10;
      rowData.forEach((cell, idx) => {
        doc.rect(xPos, yPos, colWidths[idx], rowHeight);
        // Truncar texto se necessário
        const maxChars = Math.floor(colWidths[idx] / 2);
        const text = cell.length > maxChars ? cell.substring(0, maxChars - 3) + '...' : cell;
        doc.text(text, xPos + cellPadding, yPos + lineHeight + cellPadding);
        xPos += colWidths[idx];
      });
      
      // Desenhar célula de observações com múltiplas linhas
      doc.rect(xPos, yPos, colWidths[6], rowHeight);
      obsLines.slice(0, 4).forEach((line, lineIdx) => {
        doc.text(line, xPos + cellPadding, yPos + ((lineIdx + 1) * lineHeight) + cellPadding);
      });
      
      totalPagar += diaria.valor_pagar;
      totalCobrar += diaria.valor_cobrar;
      yPos += rowHeight;
    });
    
    // Totais
    yPos += 10;
    doc.setFontSize(10);
    doc.text(`Total de Diárias: ${diariasFiltradas.length}`, 10, yPos);
    doc.text(`Total a Pagar: R$ ${totalPagar.toFixed(2).replace('.', ',')}`, 80, yPos);
    doc.text(`Total a Cobrar: R$ ${totalCobrar.toFixed(2).replace('.', ',')}`, 150, yPos);
    doc.text(`Margem Total: R$ ${(totalCobrar - totalPagar).toFixed(2).replace('.', ',')}`, 220, yPos);
    
    // Salvar PDF usando Blob para maior compatibilidade
    applyWatermarkAllPages(doc, logoDataUrl);
    const hoje = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    const pdfBlob = doc.output('blob');
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `diarias-${hoje}.pdf`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    setTimeout(() => {
      if (document.body.contains(link)) {
        document.body.removeChild(link);
      }
      URL.revokeObjectURL(url);
    }, 100);
    
    toast.success("Relatório PDF gerado com sucesso!");
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [ano, mes, dia] = dateString.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      {confirmDialog}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Diárias</h1>
          <p className="text-gray-600 dark:text-gray-300">Lançamento de diárias com cálculo automático</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => {
              setFormData({
                data: '', // Permitir seleção livre de data
                dataFim: '',
                cliente_id: '',
                prestador_id: '',
                funcao_id: '',
                horario: '',
                horario_inicio: '',
                horario_saida: '',
                jornada_padrao_horas: '',
                observacoes: '',
                loteMode: false
              });
              setProviderSearchValue(''); // Reset provider search
              setEditingId(null);
            }}>
              <Plus className="mr-2 h-4 w-4" />
              Nova Diária
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>
                {editingId ? 'Editar Diária' : (formData.loteMode ? 'Nova Diária - Lançamento em Lote' : 'Nova Diária')}
              </DialogTitle>
              <DialogDescription>
                {!editingId && !formData.loteMode && "Os valores serão calculados automaticamente com base na tabela de preços."}
                {!editingId && formData.loteMode && "Múltiplas diárias serão criadas para o período selecionado com valores calculados automaticamente."}
                {editingId && "Para alterar valores, use a função Override após salvar."}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {!editingId && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-md p-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-amber-600 rounded-full"></div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Lançamento de Diárias</p>
                  </div>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                    • Para uma única diária: preencha apenas a &quot;Data Início&quot;<br/>
                    • Para várias diárias: preencha &quot;Data Início&quot; e &quot;Data Fim&quot; para lançamento em lote
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="data">Data Início *</Label>
                  <DatePicker
                    id="data"
                    value={formData.data}
                    onChange={(value) => setFormData({...formData, data: value})}
                    placeholder="Selecione a data"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Data obrigatória</p>
                </div>
                
                {!editingId && (
                  <div>
                    <Label htmlFor="dataFim">Data Fim (Opcional)</Label>
                    <DatePicker
                      id="dataFim"
                      value={formData.dataFim}
                      onChange={(novaDataFim) => {
                        setFormData({
                          ...formData,
                          dataFim: novaDataFim,
                          loteMode: novaDataFim && novaDataFim !== formData.data
                        });
                      }}
                      minDate={formData.data}
                      placeholder="Opcional (lote)"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {formData.dataFim && formData.dataFim !== formData.data 
                        ? `🔄 Lote: ${(() => {
                            if (formData.data && formData.dataFim) {
                              const diff = Math.ceil((new Date(formData.dataFim) - new Date(formData.data)) / (1000 * 60 * 60 * 24)) + 1;
                              return `${diff} diárias`;
                            }
                            return '';
                          })()} serão criadas`
                        : 'Para lançamento em lote'
                      }
                    </p>
                  </div>
                )}
              </div>
              
              {/* Seção de Horários e Jornada */}
              <div className="bg-gray-50 dark:bg-muted/40 border border-gray-200 dark:border-border rounded-md p-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Controle de Horas (Opcional)</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Preencha para calcular automaticamente horas extras com adicional de 10%
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="horario_inicio">Horário Início</Label>
                    <Input
                      id="horario_inicio"
                      type="time"
                      value={formData.horario_inicio}
                      onChange={(e) => setFormData({...formData, horario_inicio: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="horario_saida">Horário Saída</Label>
                    <Input
                      id="horario_saida"
                      type="time"
                      value={formData.horario_saida}
                      onChange={(e) => setFormData({...formData, horario_saida: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="jornada_padrao">Jornada Padrão</Label>
                    <Select 
                      value={formData.jornada_padrao_horas ? String(formData.jornada_padrao_horas) : ''} 
                      onValueChange={(value) => setFormData({...formData, jornada_padrao_horas: value ? parseInt(value) : ''})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="8">8 horas</SelectItem>
                        <SelectItem value="10">10 horas</SelectItem>
                        <SelectItem value="12">12 horas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Preview de cálculo */}
                {formData.horario_inicio && formData.horario_saida && formData.jornada_padrao_horas && (
                  <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-md">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">📊 Preview do Cálculo</p>
                    {(() => {
                      try {
                        const inicio = new Date(`2000-01-01T${formData.horario_inicio}`);
                        const saida = new Date(`2000-01-01T${formData.horario_saida}`);
                        let diffMinutos = (saida - inicio) / 60000;
                        if (diffMinutos <= 0) diffMinutos += 24 * 60; // Virada de meia-noite
                        
                        const horasTrabalhadas = (diffMinutos / 60).toFixed(2);
                        const jornada = parseInt(formData.jornada_padrao_horas);
                        const horasNormais = Math.min(parseFloat(horasTrabalhadas), jornada);
                        const horasExtras = Math.max(parseFloat(horasTrabalhadas) - jornada, 0);
                        
                        return (
                          <div className="grid grid-cols-2 gap-2 text-xs text-amber-700 dark:text-amber-400">
                            <span>Jornada selecionada: <strong>{jornada}h</strong></span>
                            <span>Horas trabalhadas: <strong>{horasTrabalhadas}h</strong></span>
                            <span>Horas normais: <strong>{horasNormais.toFixed(2)}h</strong></span>
                            <span>Horas extras: <strong className={horasExtras > 0 ? 'text-orange-600 dark:text-orange-400' : ''}>{horasExtras.toFixed(2)}h</strong></span>
                            {horasExtras > 0 && (
                              <span className="col-span-2 text-orange-600 dark:text-orange-400 font-medium">
                                ⚠️ +10% será aplicado nas {horasExtras.toFixed(2)}h extras
                              </span>
                            )}
                          </div>
                        );
                      } catch (e) {
                        return <span className="text-red-500 dark:text-red-400 text-xs">Erro no cálculo</span>;
                      }
                    })()}
                  </div>
                )}
                
                {(formData.horario_inicio || formData.horario_saida) && !formData.jornada_padrao_horas && (
                  <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                    ⚠️ Selecione a jornada padrão para calcular horas extras
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="cliente_id">Cliente *</Label>
                <SearchableSelect
                  options={clientes.map(cliente => ({
                    value: cliente.id,
                    label: cliente.nome
                  }))}
                  value={formData.cliente_id}
                  onValueChange={(value) => setFormData({...formData, cliente_id: value})}
                  placeholder="Selecione um cliente"
                  searchPlaceholder="Buscar cliente..."
                  emptyText="Nenhum cliente encontrado"
                  className="w-full"
                />
              </div>
              
              <div>
                <Label htmlFor="prestador_id">Prestador *</Label>
                <Popover open={providerComboboxOpen} onOpenChange={setProviderComboboxOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={providerComboboxOpen}
                      className="w-full justify-between"
                    >
                      {getSelectedProviderValue()}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput 
                        placeholder="Buscar por nome ou matrícula (ex: P-000002)..." 
                        value={providerSearchValue}
                        onValueChange={setProviderSearchValue}
                      />
                      <CommandList>
                        <CommandEmpty>Nenhum prestador encontrado.</CommandEmpty>
                        <CommandGroup>
                          {getFilteredProviders().map((prestador) => (
                            <CommandItem
                              key={prestador.id}
                              value={prestador.id}
                              onSelect={() => {
                                setFormData({...formData, prestador_id: prestador.id});
                                setProviderSearchValue('');
                                setProviderComboboxOpen(false);
                              }}
                            >
                              <Check 
                                className={`mr-2 h-4 w-4 ${
                                  formData.prestador_id === prestador.id ? "opacity-100" : "opacity-0"
                                }`} 
                              />
                              {prestador.numero_cadastro && (
                                <span className="font-mono text-xs text-amber-700 dark:text-amber-400 mr-2">
                                  {prestador.numero_cadastro} —
                                </span>
                              )}
                              {prestador.nome}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              
              <div>
                <Label htmlFor="funcao_id">Função *</Label>
                <Select value={formData.funcao_id} onValueChange={(value) => setFormData({...formData, funcao_id: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma função" />
                  </SelectTrigger>
                  <SelectContent>
                    {funcoes.map((funcao) => (
                      <SelectItem key={funcao.id} value={funcao.id}>
                        {funcao.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="observacoes">Observações</Label>
                <Textarea
                  id="observacoes"
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  placeholder="Observações sobre esta diária"
                />
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingId 
                    ? 'Atualizar' 
                    : (formData.loteMode 
                      ? `Criar Lote${formData.data && formData.dataFim 
                        ? ` (${(() => {
                            const diff = Math.ceil((new Date(formData.dataFim) - new Date(formData.data)) / (1000 * 60 * 60 * 24)) + 1;
                            return diff;
                          })()} diárias)` 
                        : ''}` 
                      : 'Criar Diária'
                    )
                  }
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Override Dialog */}
      <Dialog open={isOverrideDialogOpen} onOpenChange={setIsOverrideDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Override de Valor</DialogTitle>
            <DialogDescription>
              Altere manualmente o valor desta diária. Esta ação será registrada para auditoria.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleOverrideSubmit} className="space-y-4">
            <div>
              <Label htmlFor="campo_alterado">Campo a Alterar</Label>
              <Select value={overrideData.campo_alterado} onValueChange={(value) => setOverrideData({...overrideData, campo_alterado: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="valor_pagar">Valor a Pagar</SelectItem>
                  <SelectItem value="valor_cobrar">Valor a Cobrar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="valor_novo">Novo Valor (R$)</Label>
              <Input
                id="valor_novo"
                type="number"
                step="0.01"
                min="0"
                value={overrideData.valor_novo}
                onChange={(e) => setOverrideData({...overrideData, valor_novo: e.target.value})}
                placeholder="0,00"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="motivo">Motivo da Alteração *</Label>
              <Textarea
                id="motivo"
                value={overrideData.motivo}
                onChange={(e) => setOverrideData({...overrideData, motivo: e.target.value})}
                placeholder="Explique o motivo desta alteração..."
                required
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setIsOverrideDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="destructive">
                Aplicar Override
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Filtros */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
            <div>
              <Label htmlFor="filtro-prestador">Prestador</Label>
              <SearchableSelect
                options={[
                  { value: 'todos', label: 'Todos os prestadores' },
                  ...prestadores
                    .sort(sortByName)
                    .map(prestador => ({
                      value: prestador.id,
                      label: prestador.nome
                    }))
                ]}
                value={filtros.prestador}
                onValueChange={(value) => setFiltros({...filtros, prestador: value})}
                placeholder="Todos os prestadores"
                searchPlaceholder="Buscar prestador..."
                emptyText="Nenhum prestador encontrado"
                className="w-full"
              />
            </div>
            
            <div>
              <Label htmlFor="filtro-cliente">Cliente</Label>
              <SearchableSelect
                options={[
                  { value: 'todos', label: 'Todos os clientes' },
                  ...clientes.map(cliente => ({
                    value: cliente.id,
                    label: cliente.nome
                  }))
                ]}
                value={filtros.cliente}
                onValueChange={(value) => setFiltros({...filtros, cliente: value})}
                placeholder="Todos os clientes"
                searchPlaceholder="Buscar cliente..."
                emptyText="Nenhum cliente encontrado"
                className="w-full"
              />
            </div>
            
            <div>
              <Label htmlFor="filtro-status-pago">Status Pagamento</Label>
              <Select 
                value={filtros.statusPago} 
                onValueChange={(value) => setFiltros({...filtros, statusPago: value})}
              >
                <SelectTrigger id="filtro-status-pago">
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pago">Pago</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="filtro-data-inicio">Data Início</Label>
              <DatePicker
                id="filtro-data-inicio"
                value={filtros.dataInicio}
                onChange={(value) => setFiltros({...filtros, dataInicio: value})}
                placeholder="Data início"
              />
            </div>
            
            <div>
              <Label htmlFor="filtro-data-fim">Data Fim</Label>
              <DatePicker
                id="filtro-data-fim"
                value={filtros.dataFim}
                onChange={(value) => setFiltros({...filtros, dataFim: value})}
                placeholder="Data fim"
                minDate={filtros.dataInicio}
              />
            </div>
            
            <div>
              <Button 
                variant="outline" 
                onClick={() => setFiltros({ prestador: 'todos', cliente: 'todos', statusPago: 'todos', dataInicio: '', dataFim: '' })}
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Botões de Exportação */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Exportar Diárias</CardTitle>
          <CardDescription>Exportar as diárias filtradas em diferentes formatos</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-4">
            <Button 
              onClick={exportDiariasPDF} 
              className="flex items-center space-x-2"
            >
              <FileText className="h-4 w-4" />
              <span>Exportar PDF</span>
            </Button>
            <Button 
              onClick={exportDiariasExcel} 
              variant="outline"
              className="flex items-center space-x-2"
              data-testid="exportar-diarias-excel-btn"
            >
              <Download className="h-4 w-4" />
              <span>Exportar Excel</span>
            </Button>
            <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
              Total de diárias: <span className="font-semibold ml-1">{getDiariasFiltradas().length}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Prestador</TableHead>
                <TableHead>Função</TableHead>
                <TableHead>Valor Pagar</TableHead>
                <TableHead>Valor Cobrar</TableHead>
                <TableHead>Margem</TableHead>
                <TableHead>Override</TableHead>
                <TableHead>Status Pago</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getDiariasFiltradas()
                .map((diaria) => (
                <TableRow key={diaria.id}>
                  <TableCell>{formatDate(diaria.data)}</TableCell>
                  <TableCell className="font-medium">{getClienteNome(diaria.cliente_id)}</TableCell>
                  <TableCell>{getPrestadorNome(diaria.prestador_id)}</TableCell>
                  <TableCell>{getFuncaoNome(diaria.funcao_id)}</TableCell>
                  <TableCell className="text-red-600 dark:text-red-400">{formatCurrency(diaria.valor_pagar)}</TableCell>
                  <TableCell className="text-green-600 dark:text-green-400">{formatCurrency(diaria.valor_cobrar)}</TableCell>
                  <TableCell className="font-medium">
                    {formatCurrency(diaria.valor_cobrar - diaria.valor_pagar)}
                  </TableCell>
                  <TableCell>
                    {diaria.tem_override ? (
                      <Badge variant="destructive">Alterado</Badge>
                    ) : (
                      <Badge variant="outline">Automático</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant={diaria.pago ? "default" : "outline"}
                        onClick={() => togglePagamento(diaria.id, !diaria.pago)}
                        className={diaria.pago ? "bg-green-600 hover:bg-green-700" : ""}
                      >
                        <Check className="h-4 w-4" />
                        {diaria.pago ? "Pago" : "Pendente"}
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(diaria)} data-testid={`editar-diaria-${diaria.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleOverride(diaria)} data-testid={`override-diaria-${diaria.id}`}>
                        <DollarSign className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(diaria.id)} data-testid={`excluir-diaria-${diaria.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Diarias;
